/**
 * Infinite Scroll for Shopify Dawn Collection Product Grid
 */
class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.isLoading = false;
    this.onIntersection = this.onIntersection.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  init() {
    this.gridId = this.dataset.gridId || 'product-grid';
    this.sectionId = this.dataset.sectionId || '';
    this.nextUrl = this.dataset.nextUrl || '';
    this.loadingEl = this.querySelector('.infinite-scroll__loading');
    this.endEl = this.querySelector('.infinite-scroll__end');

    // If there is no next page URL to begin with (only 1 page)
    if (!this.nextUrl) {
      if (this.endEl) this.endEl.style.display = 'block';
      if (this.loadingEl) this.loadingEl.style.display = 'none';
      return;
    }

    if (this.endEl) this.endEl.style.display = 'none';
    if (this.loadingEl) this.loadingEl.style.display = 'none';

    this.createObserver();
  }

  createObserver() {
    if (this.observer) {
      this.observer.disconnect();
    }

    // Trigger 450px before entering viewport for a seamless scroll
    const options = {
      root: null,
      rootMargin: '0px 0px 450px 0px',
      threshold: 0,
    };

    this.observer = new IntersectionObserver(this.onIntersection, options);
    this.observer.observe(this);
  }

  onIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !this.isLoading && this.nextUrl) {
        this.loadMore();
      }
    });
  }

  isElementNearViewport() {
    const rect = this.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    return rect.top <= windowHeight + 450;
  }

  async loadMore() {
    if (this.isLoading || !this.nextUrl) return;

    this.isLoading = true;
    if (this.loadingEl) this.loadingEl.style.display = 'flex';

    try {
      // Build fetch URL with section_id if available for optimal speed and smaller payload
      const fetchUrl = new URL(this.nextUrl, window.location.origin);
      if (this.sectionId) {
        fetchUrl.searchParams.set('section_id', this.sectionId);
      }

      let response = await fetch(fetchUrl.href);
      if (!response.ok) {
        // Fallback to fetching standard URL without section_id
        response = await fetch(this.nextUrl);
        if (!response.ok) throw new Error(`HTTP error ${response.status}`);
      }
      const text = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');

      // Locate the product grid items
      let newGrid = doc.getElementById(this.gridId);
      if (!newGrid && this.sectionId) {
        newGrid = doc.querySelector(`[data-id="${this.sectionId}"]`) || doc.querySelector('.product-grid');
      }
      if (!newGrid) {
        newGrid = doc.querySelector('.product-grid');
      }

      const currentGrid = document.getElementById(this.gridId) || document.querySelector('.product-grid');

      if (newGrid && currentGrid) {
        const newItems = Array.from(newGrid.querySelectorAll('.grid__item, li.grid__item'));

        if (newItems.length > 0) {
          const fragment = document.createDocumentFragment();

          newItems.forEach((item) => {
            // Cancel scroll trigger animation so newly appended items become visible immediately
            item.classList.remove('scroll-trigger--offscreen');
            item.classList.add('scroll-trigger--cancel');
            fragment.appendChild(item);
          });

          currentGrid.appendChild(fragment);

          // Re-initialize animations if present in theme
          if (typeof window.initializeScrollAnimationTrigger === 'function') {
            window.initializeScrollAnimationTrigger(currentGrid);
          }

          document.dispatchEvent(
            new CustomEvent('collection:infinite-scroll:loaded', {
              detail: { itemsCount: newItems.length, currentGrid },
            })
          );
        }
      }

      // Find the next page URL in the fetched HTML
      let nextUrl = null;
      const nextInfiniteScroll = doc.querySelector('infinite-scroll');
      if (nextInfiniteScroll && nextInfiniteScroll.dataset.nextUrl) {
        nextUrl = nextInfiniteScroll.dataset.nextUrl;
      } else {
        // Fallback: check pagination element links
        const nextLink = doc.querySelector(
          '.pagination__item--prev, .pagination__item-arrow[aria-label*="next" i], [rel="next"], .pagination__next a'
        );
        if (nextLink && nextLink.getAttribute('href')) {
          nextUrl = nextLink.getAttribute('href');
        }
      }

      if (nextUrl && nextUrl !== this.nextUrl) {
        this.nextUrl = nextUrl;
        this.dataset.nextUrl = nextUrl;
        if (this.loadingEl) this.loadingEl.style.display = 'none';

        // Check if the sentinel is still near the viewport (e.g. large monitor or fast scroll)
        setTimeout(() => {
          if (!this.isLoading && this.nextUrl && this.isElementNearViewport()) {
            this.loadMore();
          }
        }, 150);
      } else {
        // Reached the end of products
        this.nextUrl = '';
        this.dataset.nextUrl = '';

        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }

        if (this.loadingEl) this.loadingEl.style.display = 'none';
        if (this.endEl) this.endEl.style.display = 'block';
      }
    } catch (error) {
      console.error('Failed to load products with infinite scroll:', error);
      if (this.loadingEl) this.loadingEl.style.display = 'none';
    } finally {
      this.isLoading = false;
    }
  }
}

if (!customElements.get('infinite-scroll')) {
  customElements.define('infinite-scroll', InfiniteScroll);
}
